const SEARCH_FIELDS = [
  'key',
  'title',
  'subtitle',
  'author_name',
  'cover_i',
  'first_publish_year',
  'subject',
  'language',
  'isbn',
  'publisher',
  'number_of_pages_median',
  'edition_count',
  'ratings_average',
  'ratings_count',
  'want_to_read_count',
  'currently_reading_count',
  'already_read_count',
  'first_sentence',
].join(',');

export function normalizeOpenLibraryBook(item = {}) {
  if (!item.key || !item.title) return null;
  const workId = String(item.key).replace(/^\/works\//, '');
  const authors = (item.author_name || []).slice(0, 3);
  return {
    id: workId,
    workId,
    openLibraryId: item.key,
    name: item.title.trim(),
    title: item.title.trim(),
    subtitle: item.subtitle || '',
    author: authors.join(', '),
    authors,
    categories: (item.subject || []).slice(0, 30),
    subjects: (item.subject || []).slice(0, 30),
    languageCodes: (item.language || []).slice(0, 8),
    isbnList: (item.isbn || []).slice(0, 20),
    publishers: (item.publisher || []).slice(0, 8),
    firstPublishYear: item.first_publish_year || null,
    pageCountMedian: item.number_of_pages_median || null,
    editionCount: item.edition_count || null,
    ratings: { average: item.ratings_average ?? null, count: item.ratings_count ?? null },
    popularity: {
      wantToRead: item.want_to_read_count || 0,
      currentlyReading: item.currently_reading_count || 0,
      alreadyRead: item.already_read_count || 0,
    },
    firstSentence: Array.isArray(item.first_sentence) ? item.first_sentence[0] : item.first_sentence || '',
    coverUrl: item.cover_i ? `https://covers.openlibrary.org/b/id/${item.cover_i}-M.jpg` : '',
    imageUrl: item.cover_i ? `https://covers.openlibrary.org/b/id/${item.cover_i}-M.jpg` : '',
    source: 'openlibrary',
  };
}

async function fetchSubject(subject, signal, limit = 40) {
  const params = new URLSearchParams({ subject, fields: SEARCH_FIELDS, limit: String(limit) });
  const response = await fetch(`https://openlibrary.org/search.json?${params.toString()}`, { signal });
  if (!response.ok) throw new Error('Open Library search is unavailable.');
  const data = await response.json();
  return {
    total: Number(data.numFound || 0),
    books: (data.docs || []).map(normalizeOpenLibraryBook).filter(Boolean),
  };
}

async function fetchSearch(parameter, value, signal, extras = {}) {
  const params = new URLSearchParams({ [parameter]: value, fields: SEARCH_FIELDS, limit: '40', ...extras });
  const response = await fetch(`https://openlibrary.org/search.json?${params.toString()}`, { signal });
  if (!response.ok) throw new Error('Open Library search is unavailable.');
  const data = await response.json();
  return {
    total: Number(data.numFound || 0),
    books: (data.docs || []).map(normalizeOpenLibraryBook).filter(Boolean),
    expanded: false,
  };
}

export async function searchOpenLibrary(query, mode = 'name', signal) {
  const normalized = query.trim().replace(/\s+/g, ' ');
  if (mode === 'subject') return searchOpenLibraryBySubject(normalized, signal);
  if (mode === 'content') return fetchSearch('q', normalized, signal, { has_fulltext: 'true', mode: 'everything' });
  return fetchSearch('title', normalized, signal);
}

export async function searchOpenLibraryBySubject(subject, signal) {
  const normalized = subject.trim().replace(/\s+/g, ' ');
  const exact = await fetchSubject(normalized, signal);
  const stopWords = new Set(['and', 'the', 'for', 'with', 'from', 'into', 'about']);
  const terms = [...new Set(normalized.toLowerCase().split(' ').filter((term) => term.length > 2 && !stopWords.has(term)))].slice(0, 4);
  if (exact.books.length >= 12 || terms.length < 2) return { ...exact, expanded: false };

  const relatedGroups = await Promise.all(terms.map(async (term) => {
    try { return (await fetchSubject(term, signal, 20)).books; }
    catch (error) { if (error.name === 'AbortError') throw error; return []; }
  }));
  const books = [];
  const seen = new Set();
  const add = (book) => { if (!seen.has(book.id)) { seen.add(book.id); books.push(book); } };
  exact.books.forEach(add);
  for (let index = 0; books.length < 40 && relatedGroups.some((group) => index < group.length); index += 1) {
    relatedGroups.forEach((group) => { if (group[index] && books.length < 40) add(group[index]); });
  }
  return { total: exact.total, books, expanded: books.length > exact.books.length };
}

const detailText = (value) => typeof value === 'string' ? value.trim() : typeof value?.value === 'string' ? value.value.trim() : '';

export async function fetchOpenLibraryBookDetails(workId, signal, fallback = {}) {
  const id = String(workId || '').replace(/^\/works\//, '');
  if (!/^OL\d+W$/.test(id)) throw new Error('This Open Library book could not be identified.');

  const workResponse = await fetch(`https://openlibrary.org/works/${id}.json`, { signal });
  if (!workResponse.ok) throw new Error(workResponse.status === 404 ? 'This book is no longer available from Open Library.' : 'Unable to load this book from Open Library.');
  const details = await workResponse.json();

  let authors = fallback.authors || [];
  if (!authors.length && Array.isArray(details.authors)) {
    const authorKeys = details.authors.map((item) => item?.author?.key || item?.key).filter(Boolean).slice(0, 3);
    const authorResults = await Promise.all(authorKeys.map(async (key) => {
      try {
        const response = await fetch(`https://openlibrary.org${key}.json`, { signal });
        return response.ok ? (await response.json()).name || '' : '';
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        return '';
      }
    }));
    authors = authorResults.filter(Boolean);
  }

  let ratings = fallback.ratings || { average: null, count: null };
  try {
    const ratingsResponse = await fetch(`https://openlibrary.org/works/${id}/ratings.json`, { signal });
    if (ratingsResponse.ok) {
      const data = await ratingsResponse.json();
      ratings = { average: data.summary?.average ?? ratings.average ?? null, count: data.summary?.count ?? ratings.count ?? null };
    }
  } catch (error) {
    if (error.name === 'AbortError') throw error;
  }

  const coverId = (details.covers || []).find((value) => Number(value) > 0);
  const title = details.title || fallback.name || fallback.title || 'Untitled book';
  const categories = [...new Set([...(fallback.categories || fallback.subjects || []), ...(details.subjects || [])])].slice(0, 30);
  return {
    ...fallback,
    id,
    workId: id,
    openLibraryId: `/works/${id}`,
    name: title,
    title,
    subtitle: details.subtitle || fallback.subtitle || '',
    author: authors.join(', ') || fallback.author || '',
    authors,
    categories,
    subjects: categories,
    firstPublishDate: details.first_publish_date || fallback.firstPublishDate || '',
    synopsis: detailText(details.description) || fallback.synopsis || '',
    excerpt: detailText(details.excerpts?.[0]?.excerpt) || fallback.excerpt || '',
    coverUrl: fallback.coverUrl || (coverId ? `https://covers.openlibrary.org/b/id/${coverId}-L.jpg` : ''),
    imageUrl: fallback.imageUrl || fallback.coverUrl || (coverId ? `https://covers.openlibrary.org/b/id/${coverId}-L.jpg` : ''),
    ratings,
    source: 'openlibrary',
  };
}

export function bookmarkPayload(book) {
  return {
    openLibraryId: book.openLibraryId,
    workId: book.workId || book.id,
    name: book.name || book.title,
    subtitle: book.subtitle || '',
    author: book.author || (book.authors || []).join(', '),
    authors: book.authors || [],
    categories: book.categories || book.subjects || [],
    firstPublishYear: book.firstPublishYear || null,
    imageUrl: book.imageUrl || book.coverUrl || '',
    coverUrl: book.coverUrl || book.imageUrl || '',
    ratings: book.ratings || { average: null, count: null },
    popularity: book.popularity || { wantToRead: 0, currentlyReading: 0, alreadyRead: 0 },
    firstSentence: book.firstSentence || '',
    source: 'openlibrary',
  };
}
